import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { registerSchema, type RegisterInput } from '@pandacalcio/validation';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../features/auth/AuthContext';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthLayout } from '../../components/AuthLayout';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import type { PublicUser } from '../../features/auth/types';

export function RegisterPage() {
  usePageTitle('Registrati');
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { announce } = useAnnouncer();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resent, setResent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({ resolver: zodResolver(registerSchema) });

  const onSubmit = handleSubmit(async (input) => {
    setServerError(null);
    try {
      await api('/auth/register', { method: 'POST', body: input });
      // Account creato: entra subito, la verifica email non è bloccante.
      try {
        const res = await api<{ user: PublicUser }>('/auth/login', {
          method: 'POST',
          body: { email: input.email, password: input.password },
        });
        setUser(res.user);
        announce(
          `Benvenuto, ${res.user.displayName}! Account creato. Ti abbiamo inviato un'email di conferma.`,
          'assertive',
        );
        navigate('/dashboard', { replace: true });
        return;
      } catch {
        // Login automatico non riuscito: mostra comunque la conferma.
      }
      setRegisteredEmail(input.email);
      setSuccess(true);
      announce('Registrazione completata. Puoi già accedere con email e password.');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Qualcosa è andato storto. Riprova.';
      setServerError(message);
      announce(`Errore: ${message}`, 'assertive');
    }
  });

  if (success) {
    return (
      <AuthLayout title="Account creato">
        <p role="status" className="text-lg">
          Il tuo account è pronto e <strong>puoi già accedere</strong> con email e password. Ti
          abbiamo anche inviato un’email di conferma: serve solo a proteggere il tuo account, non
          è necessaria per entrare.
        </p>
        <p className="mt-6">
          <Link to="/accedi" className="text-panda-700 underline dark:text-panda-300">
            Vai alla pagina di accesso
          </Link>
        </p>
        <p className="mt-4 text-sm">
          Non trovi l’email (controlla anche lo spam)?{' '}
          {resent ? (
            <span role="status">Email inviata di nuovo.</span>
          ) : (
            <button
              type="button"
              className="min-h-11 text-panda-700 underline dark:text-panda-300"
              onClick={async () => {
                try {
                  await api('/auth/resend-verification', {
                    method: 'POST',
                    body: { email: registeredEmail },
                  });
                } catch {
                  // risposta identica in ogni caso: nessun dettaglio da mostrare
                }
                setResent(true);
                announce('Email di conferma inviata di nuovo.');
              }}
            >
              Rimanda l’email
            </button>
          )}
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Registrati">
      {serverError && (
        <div
          role="alert"
          className="mb-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800"
        >
          {serverError}
        </div>
      )}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField
          label="Nome visualizzato"
          description="Il nome che gli altri partecipanti vedranno in classifica."
          autoComplete="nickname"
          required
          error={errors.displayName?.message}
          {...register('displayName')}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <TextField
          label="Password"
          type="password"
          revealable
          description="Almeno 10 caratteri, con almeno una lettera e un numero."
          autoComplete="new-password"
          required
          error={errors.password?.message}
          {...register('password')}
        />
        <p className="text-sm text-ink-600 dark:text-ink-300">
          Registrandoti dichiari di aver letto l’{' '}
          <Link to="/privacy" className="text-panda-700 underline dark:text-panda-300">
            informativa privacy
          </Link>{' '}
          e la{' '}
          <Link to="/cookie" className="text-panda-700 underline dark:text-panda-300">
            cookie policy
          </Link>
          .
        </p>
        <Button type="submit" loading={isSubmitting} loadingText="Registrazione in corso…">
          Crea account
        </Button>
      </form>

      <p className="mt-6 text-sm">
        Hai già un account?{' '}
        <Link to="/accedi" className="text-panda-700 underline dark:text-panda-300">
          Accedi
        </Link>
      </p>
    </AuthLayout>
  );
}
